import { useId } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, Plus, X } from "lucide-react";
import {
  cn,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@nexus/ui";
import type { FilterConnector, FilterGroup, FilterOperator, FilterRule } from "@nexus/contracts";

// ─── Field + operator definitions (shared by drilldown & overview) ────────────

/** A selectable advanced-filter field. Shape matches `OverviewAdvancedFilterField`
 *  in @nexus/contracts, so callers can pass that catalog directly. */
export interface FilterFieldOption {
  key: string;
  label: string;
  type: "string" | "numeric";
}

export const STRING_OPERATORS: { label: string; value: FilterOperator }[] = [
  { label: "includes", value: "includes" },
  { label: "does not include", value: "not_includes" },
  { label: "is", value: "is" },
  { label: "is not", value: "is_not" },
  { label: "is empty", value: "is_empty" },
  { label: "is not empty", value: "is_not_empty" },
];

export const NUMERIC_OPERATORS: { label: string; value: FilterOperator }[] = [
  { label: ">", value: "gt" },
  { label: "<", value: "lt" },
  { label: "=", value: "eq" },
  { label: "≠", value: "neq" },
  { label: "≥", value: "gte" },
  { label: "≤", value: "lte" },
  { label: "is empty", value: "is_empty" },
  { label: "is not empty", value: "is_not_empty" },
];

export const NO_VALUE_OPERATORS = new Set<FilterOperator>(["is_empty", "is_not_empty"]);

// ─── Connector toggle pill ──────────────────────────────────────────────────

export function ConnectorPill({
  value,
  onChange,
  fixed,
}: {
  value: FilterConnector | "Where";
  onChange?: (v: FilterConnector) => void;
  fixed?: boolean;
}) {
  if (fixed || value === "Where") {
    return (
      <span className="inline-flex h-8 w-14 shrink-0 items-center justify-center rounded-lg border border-primary/20 bg-primary/5 text-xs font-medium text-muted-foreground">
        {value}
      </span>
    );
  }
  return (
    <Select value={value} onValueChange={(v) => onChange?.(v as FilterConnector)}>
      <SelectTrigger className="h-8 w-14 shrink-0 rounded-lg border-primary/20 bg-primary/5 px-2 text-xs font-medium hover:bg-primary/10">
        <span>{value}</span>
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="And">And</SelectItem>
        <SelectItem value="Or">Or</SelectItem>
      </SelectContent>
    </Select>
  );
}

// ─── Single filter rule row ─────────────────────────────────────────────────

export function FilterRuleRow({
  rule,
  ruleIndex,
  fieldOptions,
  onUpdate,
  onRemove,
  isFirst,
}: {
  rule: FilterRule;
  ruleIndex: number;
  fieldOptions: FilterFieldOption[];
  onUpdate: (patch: Partial<FilterRule>) => void;
  onRemove: () => void;
  isFirst: boolean;
}) {
  const fieldDef = fieldOptions.find((f) => f.key === rule.field) ?? fieldOptions[0];
  const operators = fieldDef?.type === "numeric" ? NUMERIC_OPERATORS : STRING_OPERATORS;
  const showValue = !NO_VALUE_OPERATORS.has(rule.operator);

  const handleFieldChange = (newField: string) => {
    const newFieldDef = fieldOptions.find((f) => f.key === newField);
    const newOperators = newFieldDef?.type === "numeric" ? NUMERIC_OPERATORS : STRING_OPERATORS;
    const opStillValid = newOperators.some((o) => o.value === rule.operator);
    onUpdate({
      field: newField,
      operator: opStillValid ? rule.operator : newOperators[0]!.value,
      value: "",
    });
  };

  const handleOperatorChange = (nextOperator: string) => {
    const operator = nextOperator as FilterOperator;
    // "is empty" / "is not empty" hide the input, so drop a stale value. Every other
    // condition still needs the value the user already typed (AD-2898).
    if (NO_VALUE_OPERATORS.has(operator)) {
      onUpdate({ operator, value: "" });
      return;
    }
    onUpdate({ operator });
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      {/* connector / "Where" label */}
      {isFirst ? (
        <ConnectorPill value="Where" fixed />
      ) : (
        <ConnectorPill value={rule.connector} onChange={(v) => onUpdate({ connector: v })} />
      )}

      {/* field selector */}
      <Select value={rule.field} onValueChange={handleFieldChange}>
        <SelectTrigger className="h-8 w-[140px] shrink-0 rounded-lg border-primary/20 bg-primary/5 px-3 text-xs hover:bg-primary/10">
          <span className="truncate">{fieldDef?.label ?? rule.field}</span>
        </SelectTrigger>
        <SelectContent>
          {fieldOptions.map((f) => (
            <SelectItem key={f.key} value={f.key}>
              {f.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* operator selector */}
      <Select value={rule.operator} onValueChange={handleOperatorChange}>
        <SelectTrigger className="h-8 w-[148px] shrink-0 rounded-lg border-primary/20 bg-primary/5 px-3 text-xs hover:bg-primary/10">
          <span className="truncate">{operators.find((o) => o.value === rule.operator)?.label ?? rule.operator}</span>
        </SelectTrigger>
        <SelectContent>
          {operators.map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* value input */}
      {showValue && (
        <Input
          type={fieldDef?.type === "numeric" ? "number" : "text"}
          value={rule.value}
          onChange={(e) => onUpdate({ value: e.target.value })}
          placeholder={fieldDef?.type === "numeric" ? "0" : "value…"}
          className="h-8 w-[140px] shrink-0 rounded-lg border-primary/20 bg-primary/5 px-3 text-xs placeholder:text-muted-foreground/60 focus-visible:ring-1"
        />
      )}

      {/* remove rule */}
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove filter rule ${ruleIndex + 1}`}
        className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-primary/20 bg-primary/5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

// ─── Single filter group ────────────────────────────────────────────────────

export function FilterGroupSection({
  group,
  groupIndex,
  fieldOptions,
  isFirst,
  onSetConnector,
  onAddRule,
  onRemoveGroup,
  onUpdateRule,
  onRemoveRule,
}: {
  group: FilterGroup;
  groupIndex: number;
  fieldOptions: FilterFieldOption[];
  isFirst: boolean;
  onSetConnector: (c: FilterConnector) => void;
  onAddRule: () => void;
  onRemoveGroup: () => void;
  onUpdateRule: (ruleIndex: number, patch: Partial<FilterRule>) => void;
  onRemoveRule: (ruleIndex: number) => void;
}) {
  return (
    <div className="flex items-start gap-2">
      {/* group connector — hidden for first group */}
      {!isFirst && (
        <div className="mt-1 shrink-0">
          <ConnectorPill value={group.connector} onChange={onSetConnector} />
        </div>
      )}

      {/* group body */}
      <div className="flex flex-1 flex-col gap-2 rounded-xl border border-border bg-muted/30 p-3">
        {group.rules.map((rule, rIdx) => (
          <FilterRuleRow
            key={rIdx}
            rule={rule}
            ruleIndex={rIdx}
            fieldOptions={fieldOptions}
            isFirst={rIdx === 0}
            onUpdate={(patch) => onUpdateRule(rIdx, patch)}
            onRemove={() => onRemoveRule(rIdx)}
          />
        ))}
        <button
          type="button"
          onClick={onAddRule}
          className="inline-flex h-7 w-fit items-center gap-1 rounded-lg border border-dashed border-border px-2.5 text-xs text-muted-foreground hover:border-primary/40 hover:text-foreground"
        >
          <Plus className="h-3 w-3" /> Filter
        </button>
      </div>

      {/* remove group */}
      <button
        type="button"
        onClick={onRemoveGroup}
        aria-label={`Remove filter group ${groupIndex + 1}`}
        className="mt-1 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-primary/20 bg-primary/5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

// ─── Top-level editor (header + groups) ───────────────────────────────────────

/**
 * Renders the "Advanced Filters" labeled block — one `FilterGroupSection` per group.
 * The "+ Filter" entry-point (add rule / add group) lives in the host toolbar, since
 * its placement differs between drilldown and overview. Render only when groups exist.
 *
 * Expansion is controlled by the host: the panel sits in the sticky Explore header, so
 * a preset with many rules would otherwise push the results off-screen (AD-2846). Hosts
 * collapse it on load / preset apply and expand it when the user adds a rule.
 */
export function AdvancedFiltersEditor({
  groups,
  fieldOptions,
  isExpanded,
  onExpandedChange,
  onSetConnector,
  onAddRule,
  onRemoveGroup,
  onUpdateRule,
  onRemoveRule,
  className,
}: {
  groups: FilterGroup[];
  fieldOptions: FilterFieldOption[];
  isExpanded: boolean;
  onExpandedChange: (isExpanded: boolean) => void;
  onSetConnector: (groupIndex: number, c: FilterConnector) => void;
  onAddRule: (groupIndex: number) => void;
  onRemoveGroup: (groupIndex: number) => void;
  onUpdateRule: (groupIndex: number, ruleIndex: number, patch: Partial<FilterRule>) => void;
  onRemoveRule: (groupIndex: number, ruleIndex: number) => void;
  className?: string;
}) {
  const { t } = useTranslation();
  const panelId = useId();
  if (groups.length === 0) return null;
  const ruleCount = groups.reduce((total, group) => total + group.rules.length, 0);
  return (
    <div className={cn("flex flex-col gap-2", className)} data-testid="advanced-filters-editor">
      <button
        type="button"
        onClick={() => onExpandedChange(!isExpanded)}
        aria-expanded={isExpanded}
        aria-controls={panelId}
        className="inline-flex w-fit items-center gap-2 rounded-md text-sm font-semibold text-foreground hover:text-foreground/80"
      >
        {t("explore.advancedFilters.title")}
        <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-semibold tabular-nums text-primary">
          {t("explore.advancedFilters.ruleCount", { count: ruleCount })}
        </span>
        <ChevronDown
          className={cn("h-3.5 w-3.5 text-muted-foreground transition-transform", isExpanded && "rotate-180")}
          aria-hidden
        />
      </button>
      {isExpanded && (
        <div
          id={panelId}
          className="flex flex-col gap-2 animate-in fade-in slide-in-from-top-1 duration-150"
        >
          {groups.map((group, gIdx) => (
            <FilterGroupSection
              key={gIdx}
              group={group}
              groupIndex={gIdx}
              fieldOptions={fieldOptions}
              isFirst={gIdx === 0}
              onSetConnector={(c) => onSetConnector(gIdx, c)}
              onAddRule={() => onAddRule(gIdx)}
              onRemoveGroup={() => onRemoveGroup(gIdx)}
              onUpdateRule={(rIdx, patch) => onUpdateRule(gIdx, rIdx, patch)}
              onRemoveRule={(rIdx) => onRemoveRule(gIdx, rIdx)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
